import { action, observable } from 'mobx';
import { provideSingleton, inject, bind } from '../utils/decorators';
import { IObjektVorlage, IObjekt, IArbeitsmittel } from '../models/ApiTypes';
import { AuftragStore } from './AuftragStore';
import KalibrierGegenstand from '../models/KalibrierGegenstand';
import { round } from '../utils/round';
import { IBpsReferenzkraftMesswerte } from '../models/ApiTypesBps2021';
import * as ApiTypesBps from '../models/ApiTypesBps2021';
import { ApiStore } from './ApiStore';
import * as ApiTypeFactory from '../models/ApiTypeFactory';
import { VorgangStore } from './VorgangStore';

/** Singleton für globale Eigenschaften zur BPS Kalibrierung mit Referenzkraft-Verfahren nach 2021er Richtlinie */
@provideSingleton(BpsReferenzkraftKalibrierungRili2021Store)
export class BpsReferenzkraftKalibrierungRili2021Store {

  @inject(ApiStore) apiStore: ApiStore;
  @inject(AuftragStore) auftragStore: AuftragStore;
  @inject(VorgangStore) vorgangStore: VorgangStore;

  @observable objektVorlage: IObjektVorlage | undefined = undefined;
  @observable kalibrierGegenstand: KalibrierGegenstand | undefined = undefined;
  @observable readOnly: boolean = false;

  private abrufTimeoutRef: number = 0;

  @observable datensatz: ApiTypesBps.IBpsReferenzkraftRolleMessung = ApiTypeFactory.leererDatensatzBPSReferenzkraftRili2021();

  messbereiche: Array<number> = [4, 5 , 6, 7.5, 8, 12, 25, 30, 40];

  @action clear() {
    this.datensatz = ApiTypeFactory.leererDatensatzBPSReferenzkraftRili2021();
  }

  /** Ruft mit der aktuellen Eingabe Sollwerte aus dem Back-End ab */
  async messpunkteAbrufen() {
    const { datensatz, kalibrierGegenstand } = this;
    if (kalibrierGegenstand && kalibrierGegenstand.objekt && datensatz.messbereich && datensatz.rollendurchmesser.mittelwert.links && datensatz.rollendurchmesser.mittelwert.rechts) {
      if ((datensatz.messbereich === 'klein' || datensatz.messbereich === 'kombiniert') && !datensatz.messbereichKlein) {
        return;
      }
      if ((datensatz.messbereich === 'groß' || datensatz.messbereich === 'kombiniert') && !datensatz.messbereichGross) {
        return;
      }
      let messwerte: IBpsReferenzkraftMesswerte = {
        mittelwerte: {
          links: datensatz.rollendurchmesser.mittelwert.links,
          rechts: datensatz.rollendurchmesser.mittelwert.rechts
        },
        messbereich: datensatz.messbereich,
        messbereiche: {
          klein: datensatz.messbereichKlein,
          gross: datensatz.messbereichGross
        },
        objekt: kalibrierGegenstand.objekt.objektguid
      };

      const response = await this.apiStore.bpsReferenzkraftMesspunkteRili2021(messwerte);
      if (response.ok) {
        return response;
      }
    }
    // @todo: Hinweis/Ergebnis bereinigen
    return undefined;
  }

  @bind @action setMesspunkt(ziel: ApiTypesBps.IBpsReferenzkraftRollensatzMesspunkt, anzeige: number) {
    if (ziel) {
      ziel.anzeige = anzeige;
    }
  }

  // @todo: naming, setzen wert im datensatz für beurteilung
  @bind @action setKmg(kmgGuid: string) {
    this.datensatz.kmg = kmgGuid;
  }

  @bind @action setBps(bpsGuid: string) {
    this.datensatz.objekt = bpsGuid;
  }

  /** Aktualisiert die Sollwerte für bereits angelegte Messpunkte */
  async messpunkteAktualisieren() {
    const response = await this.messpunkteAbrufen();
    const { datensatz } = this;
    if (response && response.ok) {
      const messpunkte = response.json;
      messpunkte.forEach((mp, index) => {
        this.setMesspunkt(datensatz.rollensatz.links.messpunkte[index], mp.anzeige);
        this.setMesspunkt(datensatz.rollensatz.rechts.messpunkte[index], mp.anzeige);
      });
    }
  }

  /** Anfang Beurteilungen abrufen */

  /** Ruft die Beurteilung der Messpunkte für die beiden Fahrspuren vom Backend ab */
  async beurteilungAbrufen() {
    // Kraftmessgerät aus Liste der gewählten Arbeitsmittel
    let kmg: IArbeitsmittel | undefined = this.kraftMessGeraet();
    if (!kmg) {
      return;
    }
    // Im Vorgang gewählter Bremsprüfstand
    this.setKmg(kmg.arbeitsmittelguid);
    if (!this.kalibrierGegenstand || !this.kalibrierGegenstand.objekt) {
      return;
    }
    this.setBps(this.kalibrierGegenstand.objekt.objektguid);
    const response = await this.apiStore.bpsReferenzkraftBeurteilungRili2021(this.datensatz);
    if (response && response.ok) {
      this.setBeurteilung(response.json);
    }
  }

  @action setBeurteilung(beurteilung: ApiTypesBps.IBpsReferenzkraftBeurteilungResponse) {
    beurteilung.links.forEach((e, index) => {
      this.datensatz.rollensatz.links.messpunkte[index].beurteilung = e.bewertung;
    });
    beurteilung.rechts.forEach((e, index) => {
      this.datensatz.rollensatz.rechts.messpunkte[index].beurteilung = e.bewertung;
    });
    this.datensatz.gesamtErgebnis.length = beurteilung.gesamt.length;
    beurteilung.gesamt.forEach((e, index) => {
      this.datensatz.gesamtErgebnis[index] = e;
    });
    // Nach Justage
    if (beurteilung.justage) {
      beurteilung.justage.links.forEach((e, index) => {
        this.datensatz.rollensatzJustage.links.messpunkte[index].beurteilung = e.bewertung;
      });
      beurteilung.justage.rechts.forEach((e, index) => {
        this.datensatz.rollensatzJustage.rechts.messpunkte[index].beurteilung = e.bewertung;
      });
      this.datensatz.rollensatzJustage.gesamtErgebnis.length = beurteilung.justage.gesamt.length;
      beurteilung.justage.gesamt.forEach((e, index) => {
        this.datensatz.rollensatzJustage.gesamtErgebnis[index] = e;
      });
    }
  }

  /** Ruft die Beurteilung der Messpunkte verzögert vom Back-End ab, um zu häufige Abrufe bei schnellen Änderungen zu vermeiden */
  beurteilungAbrufenDelayed() {
    if (this.abrufTimeoutRef > 0) {
      window.clearTimeout(this.abrufTimeoutRef);
      this.abrufTimeoutRef = 0;
    }

    const validateTimeoutInMs = 500;
    this.abrufTimeoutRef = window.setTimeout(
      () => {
        this.beurteilungAbrufen();
      },
      validateTimeoutInMs
    );
  }

  /** Ende Beurteilungen abrufen */

  /** Berechnet alle Referenzwerte neu, z.B. wenn ein Messwert oder der Rollendurchmesser verändert wurden */
  berechneReferenzWerte() {

    // Lambda für Berechnung der Werte zu einem gegebenen Messpunkt
    let mwb = (mw: ApiTypesBps.IBpsReferenzkraftRollensatzMesswert, f: number) => {
      if ((mw.sensor === null) || (mw.bps === null) || (isNaN(mw.sensor)) || (isNaN(mw.bps))) {
        mw.referenzwert = mw.messwertabweichung = null;
      } else {
        mw.referenzwert = round(mw.sensor * f, 0);
        mw.messwertabweichung = round(mw.bps - mw.referenzwert, 0);
      }
    };

    // Links
    let faktor = (this.datensatz.rollendurchmesser.faktor.links || 0);
    if (faktor !== 0) {
      this.datensatz.rollensatz.links.messpunkte.forEach(mp => { mp.messwerte.forEach((mw) => { mwb(mw, faktor); }); });
      if (this.datensatz.justageInfo === 'justage') {
        this.datensatz.rollensatzJustage.links.messpunkte.forEach(mp => { mp.messwerte.forEach((mw) => { mwb(mw, faktor); }); });
      }
    }
    // Rechts
    faktor = (this.datensatz.rollendurchmesser.faktor.rechts || 0);
    if (faktor !== 0) {
      this.datensatz.rollensatz.rechts.messpunkte.forEach(mp => { mp.messwerte.forEach((mw) => { mwb(mw, faktor); }); });
      if (this.datensatz.justageInfo === 'justage') {
        this.datensatz.rollensatzJustage.rechts.messpunkte.forEach(mp => { mp.messwerte.forEach((mw) => { mwb(mw, faktor); }); });
      }
    }

    if (this.datensatz.rollensatz.links.messpunkte.length === 0 || this.datensatz.rollensatz.rechts.messpunkte.length === 0) {
      return;
    }
    this.beurteilungAbrufenDelayed();
  }


  /** Berechnet die Mittelwerte für die Betrachtung des Rollendurchmessers über gewichtete Mittelwerte */
  berechneMittelwerteRolle() {
    const mwLiAlt = this.datensatz.rollendurchmesser.mittelwert.links;
    const mwReAlt = this.datensatz.rollendurchmesser.mittelwert.rechts;

    const { datensatz, kalibrierGegenstand } = this;
    const { messwerte } = this.datensatz.rollendurchmesser;

    // Gewichtete Mittelwert für Links/Rechts und Vorne/Hinten werden gemittelt
    let mwLiVorne =  (0.2 * (messwerte[0][0] || 0.0)) + 0.6 * (messwerte[0][1] || 0.0) + (0.2 * (messwerte[0][2] || 0.0));
    let mwLiHinten = (0.2 * (messwerte[1][0] || 0.0)) + 0.6 * (messwerte[1][1] || 0.0) + (0.2 * (messwerte[1][2] || 0.0));
    let mwReVorne =  (0.2 * (messwerte[2][0] || 0.0)) + 0.6 * (messwerte[2][1] || 0.0) + (0.2 * (messwerte[2][2] || 0.0));
    let mwReHinten = (0.2 * (messwerte[3][0] || 0.0)) + 0.6 * (messwerte[3][1] || 0.0) + (0.2 * (messwerte[3][2] || 0.0));
    // Bei geschweissten Rollen wird der Grunddurchmesser mit einbezogen
    if (datensatz.oberflaeche === 'geschweisst') {
      mwLiVorne = (mwLiVorne + (datensatz.rollendurchmesser.grunddurchmesser || 0.0)) / 2.0;
      mwLiHinten = (mwLiHinten + (datensatz.rollendurchmesser.grunddurchmesser || 0.0)) / 2.0;
      mwReVorne = (mwReVorne + (datensatz.rollendurchmesser.grunddurchmesser || 0.0)) / 2.0;
      mwReHinten = (mwReHinten + (datensatz.rollendurchmesser.grunddurchmesser || 0.0)) / 2.0;
    }
    this.datensatz.rollendurchmesser.mittelwert.links = (mwLiVorne + mwLiHinten) / 2.0;
    this.datensatz.rollendurchmesser.mittelwert.rechts = (mwReVorne + mwReHinten) / 2.0;
    // Bei Rollenmessung über den Umfang, durch PI teilen
    if (datensatz.messart === 'umfang') {
      this.datensatz.rollendurchmesser.mittelwert.links /= Math.PI;
      this.datensatz.rollendurchmesser.mittelwert.rechts /= Math.PI;
    }

    // Korrekturfaktor für die Berechnung der Referenzwerte der Rollensätze ermitteln
    if (kalibrierGegenstand) {
      let kFLinks = 1.0;
      let kFRechts = 1.0;
      let hlm = kalibrierGegenstand.metaAsNumber('hebelarmlänge');
      let uz = kalibrierGegenstand.metaAsNumber('übersetzungsverhältnis_zähler');
      let un = kalibrierGegenstand.metaAsNumber('übersetzungsverhältnis_nenner');
      if (hlm && uz && un && this.datensatz.rollendurchmesser.mittelwert.links) {
        let uebersetzung = uz / un;
        kFLinks = hlm / (this.datensatz.rollendurchmesser.mittelwert.links / 2.0) * uebersetzung;
      }
      if (hlm && uz && un && this.datensatz.rollendurchmesser.mittelwert.rechts) {
        let uebersetzung = uz / un;
        kFRechts = hlm / (this.datensatz.rollendurchmesser.mittelwert.rechts / 2.0) * uebersetzung;
      }
      this.datensatz.rollendurchmesser.faktor.links = kFLinks;
      this.datensatz.rollendurchmesser.faktor.rechts = kFRechts;
    }

    // Bei einer Änderung des Mittelwerte müssen alle Referenzpunkte für die Messung der Rollen neu berechnet werden
    if ((mwLiAlt !== this.datensatz.rollendurchmesser.mittelwert.links) || (mwReAlt !== this.datensatz.rollendurchmesser.mittelwert.rechts)) {
      // @todo
      this.berechneReferenzWerte();
    }
  }

  /** Ermittelt das zu nutzende Kraftmessgerät aus den im Vorgang gewählten Arbeitsmitteln */
  kraftMessGeraet() {
    if (!this.vorgangStore.vorgang) {
      return undefined;
    }
    for (const am of this.vorgangStore.vorgang.arbeitsmittel) {
      if (am.typ.toLowerCase() === 'bps_kmg') {
        return am;
      }
    }
    return undefined;
  }

  messbereichGueltig() {
    const { datensatz } = this;
    if ((datensatz.messbereich === 'klein' || datensatz.messbereich === 'kombiniert') && !datensatz.messbereichKlein) {
      return false;
    }
    if ((datensatz.messbereich === 'groß' || datensatz.messbereich === 'kombiniert') && !datensatz.messbereichGross) {
      return false;
    }
    return true;
  }

  @action setObjektVorlage(value: IObjektVorlage | undefined) {
    this.objektVorlage = value;
  }

  @action setKalibrierGegenstand(value: IObjekt | undefined) {
    if (!value) {
      // Standardwerte setzen, falls noch kein Bremsenprüfstand gewählt wurde
      this.kalibrierGegenstand = undefined;
      this.datensatz.drehzahlschlupf.mingeschwindigkeit = 4;
      this.datensatz.anzeige = 'analog';
      return;
    }
    this.kalibrierGegenstand = new KalibrierGegenstand(value);
    // Die min. Geschwindigkeit für die Beurteilung von Drehzahl und Schlupf hängt von der Fz.Art. des Prüfstands ab
    // 4 km/h bei reinen PKW-Prüfständen, 2 km/h für alle anderen (LKW und PKW/LKW kombiniert)
    let fzart = this.kalibrierGegenstand.meta('fzart');
    if (fzart && fzart.toLowerCase() !== 'pkw') {
      this.datensatz.drehzahlschlupf.mingeschwindigkeit = 2;
    } else {
      this.datensatz.drehzahlschlupf.mingeschwindigkeit = 4;
    }
    // Art der Anzeige speichern, diese hat Auswirkungen auf die Auswertung
    this.datensatz.anzeige = 'analog';
    let anzeigeart = this.kalibrierGegenstand.meta('anzeigeart');
    if (anzeigeart && anzeigeart.toLowerCase() === 'digital') {
      this.datensatz.anzeige = 'digital';
    }
  }

  @action setMesswert(x: number, y: number, value: number | null) {
    this.datensatz.rollendurchmesser.messwerte[x][y] = value;
    this.berechneMittelwerteRolle();
  }

  @action setMittelwert(position: ApiTypesBps.BpsPositionRolle, value: number | null) {
    if (position === 'links') {
      this.datensatz.rollendurchmesser.mittelwert.links = value;
    }
    if (position === 'rechts') {
      this.datensatz.rollendurchmesser.mittelwert.rechts = value;
    }
  }

  @action setRollenFaktor(links: number | null, rechts: number | null) {
    this.datensatz.rollendurchmesser.faktor.links = links;
    this.datensatz.rollendurchmesser.faktor.rechts = rechts;
  }

  @action setMesswertRollendurchmesser(x: number, y: number, value: number | null) {
    this.datensatz.rollendurchmesser.messwerte[x][y] = value;
  }

  getMesswertRollendurchmesser(x: number, y: number) {
    return this.datensatz.rollendurchmesser.messwerte[x][y];
  }

  @action setOberflaeche(value: string) {
    switch (value) {
      case 'beschichtet':
        this.datensatz.oberflaeche = 'beschichtet';
        break;
      case 'geschweisst':
        this.datensatz.oberflaeche = 'geschweisst';
        break;
      default:
        this.datensatz.oberflaeche = 'beschichtet';
    }
  }

  @action setMessart(value: string) {
    switch (value) {
      case 'durchmesser':
        this.datensatz.messart = 'durchmesser';
        break;
      case 'umfang':
        this.datensatz.messart = 'umfang';
        break;
      default:
        this.datensatz.messart = 'durchmesser';
    }
  }

  @action setGrunddurchmesser(value: number | undefined) {
    this.datensatz.rollendurchmesser.grunddurchmesser = value;
  }

  @action setMessbereich(value: string) {
    switch (value) {
      case 'klein':
        this.datensatz.messbereich = 'klein';
        break;
      case 'groß':
        this.datensatz.messbereich = 'groß';
        break;
      case 'kombiniert':
        this.datensatz.messbereich = 'kombiniert';
        break;
      default:
        this.datensatz.messbereich = undefined;
    }
  }

  @action setMessbereichKlein(value: ApiTypesBps.BpsMessBereichReichweite) {
    this.datensatz.messbereichKlein = value;
  }

  @action setMessbereichGross(value: ApiTypesBps.BpsMessBereichReichweite) {
    this.datensatz.messbereichGross = value;
  }

  @action setReadOnly(value: boolean) {
    this.readOnly = value;
  }

  @action setJustageInfo(value: string) {
    this.datensatz.justageInfo = value;
    // Bei aktiver Justage wird eine zweite Eingabe angezeigt, da die Auswahl des Messbereichs nur einmal erfolgt,
    // müssen die Messpunkte hier entsprechend der Eingabe vor der Justage dimensioniert werden
    if (this.datensatz.justageInfo === 'justage') {
      let quellen = [this.datensatz.rollensatz.links.messpunkte, this.datensatz.rollensatz.rechts.messpunkte];
      let ziele = [this.datensatz.rollensatzJustage.links.messpunkte, this.datensatz.rollensatzJustage.rechts.messpunkte];
      for (let i = 0; i < quellen.length; i++) {
        // Wenn im Ziel (Messwerte nach Justage) Messpunkte fehlen, werden diese hinzugefügt
        let quelle = quellen[i];
        let ziel = ziele[i];
        for (let j = ziel.length; j < quelle.length; j++) {
          ziel.push({
            anzeige: quelle[j].anzeige,
            mittelwert: undefined,
            mittelwertabweichung: null,
            messunsicherheit: null,
            beurteilung: null,
            messwerte: [
              { bps: quelle[j].anzeige, sensor: null, referenzwert: null, messwertabweichung: null},
              { bps: quelle[j].anzeige, sensor: null, referenzwert: null, messwertabweichung: null},
              { bps: quelle[j].anzeige, sensor: null, referenzwert: null, messwertabweichung: null}
            ]
          });
        }
      }
    }
  }

  @action setMesswerte(value: Partial<ApiTypesBps.IBpsReferenzkraftRolleMessung>) {
    const { datensatz } = this;
    // JSON.parse kann an dieser Stelle nicht verwendet werden, weil Daten auch teilweise gespeichert werden können
    // Das würde dann u.U. ein Teil-Interface erzeugen, mit dem die Eingabe nicht mehr funktioniert
    if (value.version) { datensatz.version = value.version; }
    if (value.rollendurchmesser) {
      if (value.rollendurchmesser.messwerte) { datensatz.rollendurchmesser.messwerte = value.rollendurchmesser.messwerte; }
      if (value.rollendurchmesser.mittelwert) { datensatz.rollendurchmesser.mittelwert = value.rollendurchmesser.mittelwert; }
      if (value.rollendurchmesser.grunddurchmesser) { datensatz.rollendurchmesser.grunddurchmesser = value.rollendurchmesser.grunddurchmesser; }
      if (value.rollendurchmesser.faktor) { datensatz.rollendurchmesser.faktor = value.rollendurchmesser.faktor; }
    }
    if (value.oberflaeche) { datensatz.oberflaeche = value.oberflaeche; }
    if (value.messart) { datensatz.messart = value.messart; }
    if (value.messbereich) { datensatz.messbereich = value.messbereich; }
    if (value.messbereichKlein) { datensatz.messbereichKlein = value.messbereichKlein; }
    if (value.messbereichGross) { datensatz.messbereichGross = value.messbereichGross; }
    if (value.rollensatz && value.rollensatz.links) { datensatz.rollensatz.links = value.rollensatz.links; }
    if (value.rollensatz && value.rollensatz.rechts) { datensatz.rollensatz.rechts = value.rollensatz.rechts; }
    if (value.pruefortparameter) {
      if (value.pruefortparameter.beginn) { datensatz.pruefortparameter.beginn = value.pruefortparameter.beginn; }
      if (value.pruefortparameter.ende) { datensatz.pruefortparameter.ende = value.pruefortparameter.ende; }
    }
    if (value.waegezelle) { datensatz.waegezelle = value.waegezelle; }
    if (value.drehzahlschlupf) {
      if (value.drehzahlschlupf.links) { datensatz.drehzahlschlupf.links = value.drehzahlschlupf.links; }
      if (value.drehzahlschlupf.rechts) { datensatz.drehzahlschlupf.rechts = value.drehzahlschlupf.rechts; }
    }
    if (value.kmg) { datensatz.kmg = value.kmg; }
    if (value.objekt) { datensatz.objekt = value.objekt; }
    if (value.justageInfo) { datensatz.justageInfo = value.justageInfo; }
    if (value.rollensatzJustage && value.rollensatzJustage.links) { datensatz.rollensatzJustage.links = value.rollensatzJustage.links; }
    if (value.rollensatzJustage && value.rollensatzJustage.rechts) { datensatz.rollensatzJustage.rechts = value.rollensatzJustage.rechts; }
    if (value.rollensatzJustage && value.rollensatzJustage.gesamtErgebnis) { datensatz.rollensatzJustage.gesamtErgebnis = value.rollensatzJustage.gesamtErgebnis; }
    if (value.gesamtErgebnis) { datensatz.gesamtErgebnis = value.gesamtErgebnis; }
  }
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/stores/BpsReferenzkraftKalibrierungRili2021Store.ts